import { inject } from '@angular/core';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { MessageService } from 'primeng/api';
import { EgProblemService } from '@expertgroup/shell';
// ⚠️ Rečnik se ovde čita DIREKTNO, ne kroz `CopyService`: `toastDetailFor` je čista funkcija koju njen
// spec vozi bez DI-ja, a `createProtaxLink` je izvezen baš zato da se kompozicija testira bez injektora.
// Dok ProTax ima jedan jezik i jedan rečnik, ovo je ista vrednost kroz kraću putanju. ⚠⚠ Ako kopija ikad
// postane pregaziva po okruženju ili po tenantu, OVO je jedino mesto koje bi tada laglo — i mora na servis.
import { SR } from '@/app/core/copy/sr';
import { AuthService } from '@/app/core/auth/auth.service';
import { environment } from '@/environments/environment';

/**
 * The resolver-level authentication guard's message, verbatim.
 *
 * ⚠️ A string match against another repository's literal, and it is the only discriminator that exists. The
 * guards throw a bare `GraphQLException`, which carries no inner exception — so neither the ProTax error
 * filter nor Core's masking filter touches it (both return early on a null exception) and it arrives with NO
 * `extensions` at all. There is no code to key on.
 *
 * The `PROTAX_*` codes that do exist are not a way out: they are computed by `ProtaxGraphQLErrorFilter`, and
 * on the paths where they would be set Core's filter rebuilds the error with a fresh extensions dictionary and
 * drops them. `ErrorFilterTests` asserts them and passes — against a harness that omits
 * `ConfigureExpertGroupGraphQL`, so it measures a stack that does not ship.
 *
 * Changing this sentence on the server silently turns an expired session back into a dead screen.
 */
export const AUTHENTICATION_REQUIRED = 'Authentication is required.';

/** Prefix of the three ownership refusals. Same caveat as above: a message match, because nothing else survives. */
const NOT_AUTHORIZED = 'Not authorized';

/**
 * Messages that carry no information, so they must not be shown as though they did.
 *
 * - `An unexpected error occurred.` — ExpertGroup.Core's `ExtendedErrorFilter` mask (note the trailing period;
 *   `ApiExceptionMapping` uses the same phrase WITHOUT one as a REST title, and a `Set` miss is silent).
 * - `Unexpected Execution Error` — HotChocolate's own mask.
 *
 * Both mean to a user exactly what the interceptor's `>= 500` branch means, so they get its copy.
 */
const SERVER_MASKS = new Set<string>(['An unexpected error occurred.', 'Unexpected Execution Error']);

/**
 * The Serbian toast for one GraphQL error message. Exported so it can be tested without a link at all.
 *
 * ⚠️ An ownership refusal is translated rather than passed through. The server sends
 * `Not authorized to read the asset register of business entity 7.` — English, carrying an internal id, to a
 * Serbian UI. REST is no better for the same refusal (`User does not have rights on entity 7`), so this is not
 * a new inconsistency but it is a real one, and the honest fix is on the server for both surfaces at once.
 * Until then the GraphQL side at least reads like the rest of the application.
 */
export function toastDetailFor(message: string | undefined): string {
    if (!message) return SR['core.error.generic'];
    if (SERVER_MASKS.has(message)) return SR['core.error.serverError'];
    if (message.startsWith(NOT_AUTHORIZED)) return SR['core.error.notAuthorizedEntity'];

    return message;
}

/**
 * Composes the ProTax Apollo link chain.
 *
 * Exported so a spec can drive the REAL composition against a stub transport, rather than rebuilding an
 * equivalent one — because the ordering below fails SILENTLY and a hand-rolled chain in a test would not.
 *
 * ⚠️ ORDER IS LOAD-BEARING. `HttpLinkHandler.request` takes one parameter, which makes it terminating, and
 * `ApolloLink.concat` returns the FIRST link unchanged — with nothing but a dev-mode warning — when the first
 * link terminates. Put the transport first and the error link is simply discarded: no build error, no
 * exception, no toast, ever.
 *
 * `problems` receives the same failure the toast shows, so the assistant can offer to explain it after the toast
 * is gone. Required, not optional: an optional parameter here would let a caller forget it and nothing would
 * say so.
 */
export function createProtaxLink(transport: ApolloLink, messages: MessageService, auth: AuthService, problems: Pick<EgProblemService, 'report'>): ApolloLink {
    const errorLink = onError(({ graphQLErrors, networkError }) => {
        // Transport failures belong to `errorInterceptor`, which has already toasted — or redirected on a 401
        // — and re-thrown by the time this runs. Reporting here too would give one failure two toasts.
        //
        // ⚠️ The guard is DEFENSIVE rather than currently load-bearing, and the distinction is worth keeping
        // straight. Apollo populates `graphQLErrors` inside its network-error callback as well, from
        // `networkError.result.errors` — but apollo-angular hands over Angular's `HttpErrorResponse`, which
        // carries its body on `.error`, not `.result`. So today that path yields no `graphQLErrors` and the
        // next guard would return anyway. Swap the transport for a batch link or Apollo's own fetch-based one
        // and this line is the only thing standing between a 403-with-errors and a double toast. It must also
        // stay FIRST: checking `graphQLErrors` first reads more naturally and quietly removes the protection.
        if (networkError) return;
        if (!graphQLErrors?.length) return;

        // `/graphql` is NOT transport-gated: the endpoint is mapped anonymously and each resolver's own first
        // line is the whole gate. So an expired token produces HTTP 200 + this error instead of a 401, and the
        // interceptor's 401 branch never fires for it. Mirror that branch exactly — re-login, no toast.
        // `AuthService.login` latches, so several failing operations in one tick trigger one redirect.
        if (graphQLErrors.some((error) => error.message === AUTHENTICATION_REQUIRED)) {
            auth.login();
            return;
        }

        // The first error only. A rejected operation in this schema carries one; reporting every entry would
        // stack toasts for what the user experiences as a single failure.
        const detail = toastDetailFor(graphQLErrors[0]?.message);
        messages.add({ severity: 'error', summary: SR['core.toast.error'], detail });

        // ⚠️ The problem's title is the text the TOAST showed, not the raw message: for a server mask or an
        // ownership refusal the raw one is English with an internal id, and the assistant builds the suggested
        // question the user sees from `title`. The expired-session branch above reports nothing — it is a
        // redirect, not a problem on the screen, exactly like the interceptor's 401.
        problems.report({ source: 'graphql', title: detail });
    });

    return ApolloLink.from([errorLink, transport]);
}

/**
 * Apollo client for the ProTax GraphQL API.
 *
 * ⚠️ <b>No auth link, deliberately.</b> `apollo-angular/http` sends through Angular's `HttpClient`, so every
 * operation already passes through `authInterceptor`, which attaches the EG bearer token to every outbound
 * request with no URL filter. A `setContext` auth link here would set the same header the interceptor then
 * overwrites — two places to change, one of which would eventually be missed.
 *
 * `errorInterceptor` covers the same path for TRANSPORT failures — and only those. A REJECTED operation is
 * HTTP 200 with an `errors` array, which no HTTP interceptor can see. Until the error link above, every one of
 * those failed in silence: all three call sites are `try/finally` with no `catch`, so the rejection escaped to
 * the global error listener and the screen simply showed nothing.
 *
 * ⚠️ All three reads pin `fetchPolicy: 'network-only'`. A cache hit never reaches a link, so an operation
 * served from cache would bypass this error handling entirely — the design depends on those three literals
 * staying where they are.
 */
export const graphqlProvider = provideApollo(() => {
    // All four injections happen HERE, in the factory's injection context — `provideApollo` registers it as a
    // `useFactory` with no `deps`, which is why the pre-existing `inject(HttpLink)` works. The `onError`
    // callback runs per operation, long after this has returned; an `inject()` in there throws NG0203.
    const httpLink = inject(HttpLink);
    const messages = inject(MessageService);
    const auth = inject(AuthService);
    const problems = inject(EgProblemService);

    return {
        link: createProtaxLink(httpLink.create({ uri: environment.graphqlEndpoint }), messages, auth, problems),
        cache: new InMemoryCache()
    };
});
